import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import { mascot } from '../../data/mock';

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isLangOpen, setIsLangOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  const languages = [
    { code: 'es', label: 'ES', flagCode: 'es' },
    { code: 'en', label: 'EN', flagCode: 'gb' },
    { code: 'fr', label: 'FR', flagCode: 'fr' },
    { code: 'de', label: 'DE', flagCode: 'de' },
    { code: 'it', label: 'IT', flagCode: 'it' },
    { code: 'pt', label: 'PT', flagCode: 'pt' },
    { code: 'zh', label: 'ZH', flagCode: 'cn' },
    { code: 'hi', label: 'HI', flagCode: 'in' },
    { code: 'ar', label: 'AR', flagCode: 'sa' },
    { code: 'ja', label: 'JA', flagCode: 'jp' },
    { code: 'ko', label: 'KO', flagCode: 'kr' }
  ];

  const currentLang = languages.find(l => i18n.language?.startsWith(l.code)) || languages[0];

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Change to white only when we've scrolled past most of the hero section
      setScrolled(scrollY > window.innerHeight - 100);
    };

    handleScroll(); // Initial check on mount

    window.addEventListener('scroll', handleScroll);
    const container = document.getElementById('main-scroll-container');
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const navLinks = [
    { name: t('header.howItWorks'), href: '#how-it-works' },
    { name: t('header.tripPlanner'), href: '#trip-planner' },
    { name: t('header.pricing'), href: '#pricing' },
  ];

  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled
        ? 'py-3.5 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-[0_4px_30px_rgba(0,0,0,0.02)]'
        : 'py-6 bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 relative">
          
          {/* Logo with Mascot */}
          <div
            className="flex items-center gap-2.5 cursor-pointer lg:flex-1 z-10"
            onClick={() => navigate('/')}
            data-cursor-text="Home"
          >
            <motion.div
              className={`w-14 h-14 rounded-lg flex items-center justify-center p-1 border transition-colors duration-300 ${scrolled ? 'bg-slate-50 border-slate-200' : 'bg-slate-800/80 border-slate-700'}`}
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={mascot.main}
                alt="APEKAI Places Logo"
                className="w-full h-full object-contain"
              />
            </motion.div>
            <span className={`text-2xl font-extrabold tracking-tight transition-colors duration-300 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              APEKAI Places
            </span>
          </div>

          {/* Minimalist Navigation */}
          <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-8 z-10">
            {navLinks.map((link, index) => (
              <a
                key={link.name}
                href={link.href}
                className={`relative text-sm font-semibold transition-colors duration-300 py-1 ${scrolled ? 'text-slate-500 hover:text-slate-900' : 'text-white/80 hover:text-white'}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {link.name}
                {/* Thin animated line underneath */}
                <motion.span
                  className={`absolute bottom-0 left-0 right-0 h-[1.5px] origin-left ${scrolled ? 'bg-slate-900' : 'bg-white'}`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: hoveredIndex === index ? 1 : 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </a>
            ))}
          </nav>

          {/* Minimalist Black CTA Button and Lang Selector */}
          <div className="hidden lg:flex items-center justify-end gap-4 lg:flex-1 z-10 relative">
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center gap-2 font-bold rounded-full px-4 py-2.5 text-sm uppercase transition-all duration-300 border shadow-sm hover:scale-105 active:scale-95 ${scrolled ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50' : 'bg-slate-800/80 border-slate-700 text-white hover:bg-slate-800'}`}
              >
                <img src={`https://flagcdn.com/w20/${currentLang.flagCode}.png`} alt={currentLang.label} className="w-[18px] h-auto rounded-sm object-cover" />
                <span>{currentLang.label}</span>
              </button>
              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-[calc(100%+0.5rem)] right-0 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 w-32 flex flex-col items-center"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`w-full py-2.5 px-4 text-sm font-semibold flex items-center justify-start gap-3 hover:bg-slate-50 transition-colors ${i18n.language?.startsWith(lang.code) ? 'text-[#0DA2E7] bg-blue-50/50' : 'text-slate-600'}`}
                      >
                        <img src={`https://flagcdn.com/w20/${lang.flagCode}.png`} alt={lang.label} className="w-5 h-auto rounded-[2px] shadow-sm object-cover" />
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <a
              href="#download"
              data-cursor-text={t('header.downloadApp')}
              className={`font-semibold rounded-full px-8 py-3.5 text-sm tracking-wider uppercase shadow-sm border-none transition-all duration-300 ${scrolled ? 'bg-slate-900 hover:bg-slate-800 text-white' : 'bg-white text-[#0DA2E7] hover:bg-white/90'}`}
            >
              {t('header.downloadApp')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-full hover:bg-white/10 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={`w-5 h-5 ${scrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-5 h-5 ${scrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
            >
              <nav className="flex flex-col gap-1 py-4 mt-2 border-t border-slate-100">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className="text-slate-600 hover:text-slate-900 font-semibold py-3 px-4 rounded-xl hover:bg-slate-50 transition-all text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <div className="pt-4 mt-2 border-t border-slate-100 flex flex-col gap-2">
                  <div className="flex justify-center gap-2 flex-wrap mb-4 px-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => i18n.changeLanguage(lang.code)}
                        className={`px-3 py-1.5 text-sm font-bold flex items-center gap-1.5 rounded-full border shadow-sm transition-all ${i18n.language?.startsWith(lang.code) ? 'bg-[#0DA2E7] text-white border-[#0DA2E7]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                      >
                        <img src={`https://flagcdn.com/w20/${lang.flagCode}.png`} alt={lang.label} className="w-[18px] h-auto rounded-[2px] shadow-sm object-cover" />
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                  <a
                    href="#download"
                    onClick={() => setIsMenuOpen(false)}
                    className="bg-slate-900 text-white font-semibold rounded-xl py-4 text-xs uppercase tracking-wider text-center"
                  >
                    {t('header.downloadApp')}
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
